import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { VpIconButton } from '@vtmn-play/react/layered';
import VpSearchIcon from '@vtmn-play/icons/react/VpSearchIcon';
import VpArrowLeftIcon from '@vtmn-play/icons/react/VpArrowLeftIcon';
import VpFileIcon from '@vtmn-play/icons/react/VpFileIcon';
import VpShoppingCartIcon from '@vtmn-play/icons/react/VpShoppingCartIcon';
import VpTruckIcon from '@vtmn-play/icons/react/VpTruckIcon';
import VpHomeIcon from '@vtmn-play/icons/react/VpHomeIcon';
import VpCloseIcon from '@vtmn-play/icons/react/VpCloseIcon';
import VpMenuIcon from '@vtmn-play/icons/react/VpMenuIcon';

import useClickOutside from '../../utils/hooks/useClickOutside';
import useStringState from '../../utils/hooks/useStringState';
import { homePageSearchAndGetRedirectPath } from '../../business/helpers';
import { LoaderContext } from '../../utils/loader/LoaderProvider';
import { LazyReact } from '../LazyReact/LazyReact';
import { getAppRoute, RoutesPath } from '../../core/routes';
import { DEFAULT_ACTIVE_TAB } from '../../pages/SparePartOrders/constants';
import { pushMenuEventGTM } from '../../utils/GTM/googleTagManager';
import { formatAPICLink } from '../../utils/utils';
import { MobileScan } from '../shared/MobileScan/MobileScan';
import { SearchBarVp } from '../shared/Searches/SearchBar-VP';
import useToaster from '../../utils/hooks/useToaster';

import styles from './ToolbarTopMobile.module.css';
import './toolbar-top-mobile.scss';

const SingleScanner = React.lazy(() => import('../Scanner/SingleScanner'));

export const ToolbarTopMobile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [displayMenu, setDisplayMenu] = useState(false);
  const [displaySearchBar, setDisplaySearchBar] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const [userInputSearch, setUserInputSearch] = useStringState('');
  const [isHomePage, setIsHomePage] = useState(location.pathname === getAppRoute(RoutesPath.homePage));
  const toaster = useToaster();

  const inputRef = useRef<HTMLInputElement>(null);
  const searchRef = useClickOutside<HTMLDivElement>(setDisplaySearchBar);
  const { showLoader, hideLoader } = useContext(LoaderContext);

  useEffect(() => setIsHomePage(location.pathname === getAppRoute(RoutesPath.homePage)), [location]);

  const handleNavigationClick = (eventLabel: string) => {
    pushMenuEventGTM(eventLabel);

    setDisplayMenu(false);
  };

  const toggleSearchBar = (display: boolean) => {
    setDisplaySearchBar(display);
    if (display) {
      inputRef.current?.focus();
    } else {
      inputRef.current?.blur();
    }
  };

  const handleSearch = async (input: string | null = userInputSearch) => {
    setScannerActive(false);
    if (input) {
      showLoader();
      try {
        const path = await homePageSearchAndGetRedirectPath(input, isHomePage, toaster.addErrorToast);
        if (path) {
          setUserInputSearch('');
          toggleSearchBar(false);
          navigate(path.pathname);
        }
      } finally {
        hideLoader();
      }
    }
  };

  const placeholder = isHomePage ? t('home.page.searchbar.placeholder') : t('toolbar.top.searchbar.placeholder');

  const handleEnterKey = (event: React.KeyboardEvent, fn: () => void) => {
    if (event.key === 'Enter') {
      fn();
    }
  };

  return (
    <>
      {displayMenu ? (
        <button
          type="button"
          className={clsx(styles.overlay, 'reset-button-styles')}
          onClick={() => setDisplayMenu(false)}
          aria-label="overlay"
          data-testid="overlay"
        />
      ) : null}
      <div className={clsx('deployed-menu', { hidden: !displayMenu })}>
        <VpIconButton
          className="deployed-menu-close"
          onClick={() => setDisplayMenu(false)}
          data-testid="close-deployed-menu"
          size="small"
          aria-label={t('toolbar.top.mobile.menu.close')}
        >
          <VpCloseIcon />
        </VpIconButton>
        <div className="deployed-menu-navigation">
          <NavLink
            className={navData => clsx('link-menu', { selected: navData.isActive })}
            to={getAppRoute(RoutesPath.homePage)}
            onClick={() => handleNavigationClick('home')}
          >
            <VpIconButton>
              <VpHomeIcon />
            </VpIconButton>
            <span className="menu-navigation-label">{t('toolbar.top.mobile.menu.navigation.home')}</span>
          </NavLink>
          <NavLink
            className={navData => clsx('link-menu', { selected: navData.isActive })}
            to={getAppRoute(RoutesPath.shippingPage)}
            onClick={() => handleNavigationClick('parcels.flow')}
          >
            <VpIconButton>
              <VpTruckIcon />
            </VpIconButton>
            <span className="menu-navigation-label">{t('toolbar.top.mobile.menu.navigation.parcel.flows')}</span>
          </NavLink>
          <NavLink
            className={navData => clsx('link-menu', { selected: navData.isActive })}
            to={{ pathname: getAppRoute(RoutesPath.sparePartOrders), search: `?activeTab=${DEFAULT_ACTIVE_TAB}` }}
            onClick={() => handleNavigationClick('order')}
          >
            <VpIconButton>
              <VpShoppingCartIcon />
            </VpIconButton>
            <span className="menu-navigation-label">{t('toolbar.menu.order')}</span>
          </NavLink>

          <NavLink
            className={navData => clsx('link-menu', { selected: navData.isActive })}
            to={getAppRoute(RoutesPath.massiveInvoicePage)}
            onClick={() => handleNavigationClick('massive_invoice')}
          >
            <VpIconButton>
              <VpFileIcon />
            </VpIconButton>
            <span className="menu-navigation-label">{t('toolbar.top.mobile.menu.navigation.massive.invoice')}</span>
          </NavLink>
        </div>
      </div>

      <div className={clsx('toolbar-top-mobile', { home: isHomePage })} ref={searchRef}>
        <VpIconButton
          variant="ghost"
          aria-label="burger-menu"
          onClick={() => setDisplayMenu(true)}
          onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => handleEnterKey(e, () => setDisplayMenu(true))}
          data-testid="burger-menu"
        >
          <VpMenuIcon size={24} />
        </VpIconButton>

        <div className="toolbar-top-mobile-title">ICARE</div>

        <VpIconButton variant="ghost" onClick={() => toggleSearchBar(true)} size="small">
          <VpSearchIcon />
        </VpIconButton>

        <div className={clsx('search-bar white', { display: displaySearchBar })}>
          <VpIconButton variant="ghost" onClick={() => toggleSearchBar(false)} size="small">
            <VpArrowLeftIcon onClick={() => toggleSearchBar(false)} data-testid="search-bar-arrow" />
          </VpIconButton>
          <SearchBarVp
            value={userInputSearch}
            onSearch={handleSearch}
            onChange={event => setUserInputSearch(formatAPICLink(event.target.value.replace(/\s/g, '')))}
            placeholder={placeholder}
            id="search-bar-mobile"
            isWhiteBackground
          />
          <MobileScan onScanClick={() => setScannerActive(true)} />
        </div>
        {scannerActive ? (
          <LazyReact>
            <SingleScanner closeScan={() => setScannerActive(false)} action={handleSearch} />
          </LazyReact>
        ) : null}
      </div>
    </>
  );
};
